/* eslint-disable complexity */
import { createContext, useCallback, useEffect, useState } from 'react'

import { invalidateGroupCommunitiesEventDispatch } from '@hotmart/space-lib-events/dist/invalidateGroupCommunitiesQuery'
import { ToastPosition, ToastType } from '@hotmart/space-lib-events/dist/toast/enum'
import { resetFormGeneralConfigEventListener } from '@space-lib-events/resetFormGeneralConfig'
import { toastEventDispatch } from '@space-lib-events/toast'
import { toggleCommunityConfigurationSidebarEventDispatch } from '@space-lib-events/toggleCommunityConfigurationSidebar'
import { useTranslation } from 'react-i18next'
import type { EditCommunityProps } from 'src/modules/community/types'
import { EDIT_COMMUNITY_SUCCESS } from 'src/modules/community/types'
import { DataHubService } from 'src/modules/datahub'
import { EditCommunitySchema } from 'src/modules/datahub/schemas/settings'

import type { SettingsContextProps, SettingsProviderProps } from './types'

export const SettingsContext = createContext<SettingsContextProps>({} as SettingsContextProps)

const SettingsProvider = ({
  children,
  community,
  membershipSlug,
  invalidateCommunityData,
  generalConfigFlags
}: SettingsProviderProps) => {
  const { t } = useTranslation()
  const [icon, setIcon] = useState(community?.icon ?? undefined)
  const [groupId, setGroupId] = useState<number>(community?.idGroup ?? 0)
  const [communityName, setCommunityName] = useState(community?.name ?? '')
  const [description, setDescription] = useState(community?.description ?? '')
  const [settingsFlags, setSettingsFlags] = useState<Record<string, boolean>>()
  const [isCollaborativeContent, setIsCollaborativeContent] = useState(
    community?.isCollaborative ?? true
  )
  const [isDefaultEnableComments, setIsDefaultEnableComments] = useState(
    community?.isDefaultEnableComments ?? true
  )

  const [isHide, setIsHide] = useState(community?.isHide ?? false)

  const isDataValid =
    communityName.trim().length > 0 &&
    description.trim().length > 0 &&
    (community?.icon !== icon ||
      community?.idGroup !== groupId ||
      community.name !== communityName ||
      community.description !== description ||
      community.isCollaborative !== isCollaborativeContent ||
      community.isDefaultEnableComments !== isDefaultEnableComments ||
      community.isHide !== isHide)

  const resetForm = useCallback(() => {
    setIcon(community?.icon ?? undefined)
    setGroupId(community?.idGroup ?? 0)
    setCommunityName(community?.name ?? '')
    setDescription(community?.description ?? '')
    setIsCollaborativeContent(community?.isCollaborative ?? true)
    setIsDefaultEnableComments(community?.isDefaultEnableComments ?? true)
    setIsHide(community?.isHide ?? false)
  }, [community])

  const onSuccessEditCommunity = async (payload: EditCommunityProps) => {
    await invalidateCommunityData()
    invalidateGroupCommunitiesEventDispatch()

    resetForm()

    const schema = new EditCommunitySchema({
      communityId: community?.id as number,
      communityName
    })

    DataHubService.sendEvent({ schema })

    toggleCommunityConfigurationSidebarEventDispatch({ action: 'close' })

    window.dispatchEvent(new CustomEvent(EDIT_COMMUNITY_SUCCESS, { detail: payload }))

    toastEventDispatch({
      type: ToastType.SUCCESS,
      message: t('success_message', { ns: 'settings' }),
      position: ToastPosition.BOTTOM_RIGHT
    })
  }

  const onErrorEditCommunity = (error) => {
    toastEventDispatch({
      type: ToastType.DANGER,
      message: t('error_message', { ns: 'settings' }),
      position: ToastPosition.BOTTOM_RIGHT
    })

    const schema = new EditCommunitySchema({
      communityId: community?.id as number,
      failureKind: String(error?.message),
      isSuccess: false,
      communityName
    })

    DataHubService.sendEvent({ schema })
  }

  useEffect(() => {
    generalConfigFlags && setSettingsFlags(generalConfigFlags)
  }, [generalConfigFlags])

  useEffect(() => {
    if (!community || Object.keys(community).length === 0) return

    resetForm()
  }, [community, resetForm])

  useEffect(() => {
    resetFormGeneralConfigEventListener(() => {
      resetForm()
    })
  }, [resetForm])

  return (
    <SettingsContext.Provider
      value={{
        membershipSlug,
        resetForm,
        community,
        communityName,
        description,
        groupId,
        setIcon,
        setCommunityName,
        setGroupId,
        onErrorEditCommunity,
        setDescription,
        icon,
        setIsCollaborativeContent,
        onSuccessEditCommunity,
        setIsDefaultEnableComments,
        setIsHide,
        isCollaborativeContent,
        isDefaultEnableComments,
        isHide,
        isDataValid,
        settingsFlags
      }}>
      {children}
    </SettingsContext.Provider>
  )
}

export default SettingsProvider
